import { useEffect, useState } from 'react'
import {
  Box,
  Button,
  ButtonGroup,
  Collapse,
  Divider,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import Grid from '@mui/material/Grid2'
import FavoriteIcon from '@mui/icons-material/Favorite'
import HomeIcon from '@mui/icons-material/Home'
import PetsIcon from '@mui/icons-material/Pets'
import LiquorIcon from '@mui/icons-material/Liquor'
import TuneIcon from '@mui/icons-material/Tune'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'

const categoryButtonStyles = {
  all: { activeColor: 'white', inactiveColor: 'darkgreen' },
  ash: { activeColor: 'white', inactiveColor: 'pink' },
  dog: { activeColor: 'white', inactiveColor: 'brown' },
  house: { activeColor: 'white', inactiveColor: '#357EC7' },
}

export default function FilterControls({
  filterParams,
  handleFilterButtons,
  resetFilterParams,
  onAccept,
  resultCount,
  collapsible = false,
}) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const shouldCollapse = collapsible && isMobile
  const [isExpanded, setIsExpanded] = useState(!shouldCollapse)

  useEffect(() => {
    setIsExpanded(!shouldCollapse)
  }, [shouldCollapse])

  const getCategoryButton = (button, category) => {
    const isActive = filterParams.category === category

    return {
      variant: isActive ? 'contained' : 'outlined',
      color: isActive
        ? categoryButtonStyles[button].activeColor
        : categoryButtonStyles[button].inactiveColor,
    }
  }

  const categoryButtons = {
    all: getCategoryButton('all', 'ALL'),
    ash: getCategoryButton('ash', 'ASH'),
    dog: getCategoryButton('dog', 'DOG'),
    house: getCategoryButton('house', 'HOUSE'),
  }

  const sortButtons = {
    new: filterParams.sortOrder === 'NEW' ? 'contained' : 'outlined',
    az: filterParams.sortOrder === 'AZ' ? 'contained' : 'outlined',
    za: filterParams.sortOrder === 'ZA' ? 'contained' : 'outlined',
    random: filterParams.sortOrder === 'RANDOM' ? 'contained' : 'outlined',
  }

  const hasActiveFilters =
    filterParams.category !== 'ALL' ||
    filterParams.sortOrder !== 'NEW' ||
    Boolean(filterParams.searchTerm) ||
    Object.values(filterParams.booze).some(
      (booze) => booze.variant === 'contained',
    )
  const activeFilterCount =
    (filterParams.category !== 'ALL' ? 1 : 0) +
    (filterParams.sortOrder !== 'NEW' ? 1 : 0) +
    (filterParams.searchTerm ? 1 : 0) +
    Object.values(filterParams.booze).filter(
      (booze) => booze.variant === 'contained',
    ).length

  const toggleBoozeButtons = (value) => {
    const boozeKey = Object.keys(filterParams.booze).find(
      (key) => filterParams.booze[key].key === value,
    )

    if (!boozeKey) {
      return
    }

    const currentBooze = filterParams.booze[boozeKey]
    const nextVariant =
      currentBooze.variant === 'outlined' ? 'contained' : 'outlined'

    handleFilterButtons({
      ...filterParams,
      booze: {
        ...filterParams.booze,
        [boozeKey]: {
          ...currentBooze,
          variant: nextVariant,
        },
      },
    })
  }

  const handleCategoryChange = (value) => {
    handleFilterButtons({ ...filterParams, category: value })
  }

  const handleSortChange = (value) => {
    handleFilterButtons({ ...filterParams, sortOrder: value })
  }

  return (
    <Box
      component="section"
      aria-label="Drink filters"
      sx={{
        px: { xs: 1.5, sm: 2 },
        py: 1.5,
        backgroundColor: 'whitesmoke',
      }}
    >
      {shouldCollapse && (
        <Grid
          container
          spacing={1}
          sx={{
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Button
            variant={hasActiveFilters ? 'contained' : 'outlined'}
            size="small"
            startIcon={<TuneIcon />}
            endIcon={isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            aria-expanded={isExpanded}
            onClick={() => setIsExpanded((current) => !current)}
          >
            Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
          </Button>
          {typeof resultCount === 'number' && (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {resultCount} {resultCount === 1 ? 'drink' : 'drinks'}
            </Typography>
          )}
        </Grid>
      )}

      <Collapse in={isExpanded} timeout="auto" unmountOnExit={shouldCollapse}>
        <Grid
          container
          spacing={2}
          sx={{
            alignItems: 'flex-start',
            justifyContent: 'center',
            mt: shouldCollapse ? 1.5 : 0,
          }}
        >
          <Grid>
            <Typography
              variant="subtitle2"
              sx={{ mb: 0.75, textAlign: 'center' }}
            >
              Favorites
            </Typography>
            <Box
              key="category"
              aria-label="Category group"
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                gap: 1,
                width: { xs: 300, sm: 300 },
              }}
            >
              <Button
                size="small"
                variant={categoryButtons.all.variant}
                onClick={() => handleCategoryChange('ALL')}
                startIcon={
                  <LiquorIcon
                    fontSize="small"
                    sx={{ color: categoryButtons.all.color }}
                  />
                }
              >
                All Drinks
              </Button>
              <Button
                size="small"
                variant={categoryButtons.ash.variant}
                onClick={() => handleCategoryChange('ASH')}
                startIcon={
                  <FavoriteIcon
                    fontSize="small"
                    sx={{ color: categoryButtons.ash.color }}
                  />
                }
              >
                Ashley's
              </Button>
              <Button
                size="small"
                variant={categoryButtons.dog.variant}
                onClick={() => handleCategoryChange('DOG')}
                startIcon={
                  <PetsIcon
                    fontSize="small"
                    sx={{ color: categoryButtons.dog.color }}
                  />
                }
              >
                Irresistibulls
              </Button>
              <Button
                size="small"
                variant={categoryButtons.house.variant}
                onClick={() => handleCategoryChange('HOUSE')}
                startIcon={
                  <HomeIcon
                    fontSize="small"
                    sx={{ color: categoryButtons.house.color }}
                  />
                }
              >
                House
              </Button>
            </Box>
          </Grid>

          <Grid sx={{ maxWidth: 280 }}>
            <Typography
              variant="subtitle2"
              sx={{ mb: 0.75, textAlign: 'center' }}
            >
              Booze Type
            </Typography>
            <Grid
              container
              key="booze"
              aria-label="Booze type group"
              spacing={1}
              sx={{ flexWrap: 'wrap', justifyContent: 'center' }}
            >
              <Button
                key="VODKA"
                size="small"
                variant={filterParams.booze.vodka.variant}
                onClick={() => toggleBoozeButtons('VODKA')}
              >
                Vodka
              </Button>
              <Button
                key="GIN"
                size="small"
                variant={filterParams.booze.gin.variant}
                onClick={() => toggleBoozeButtons('GIN')}
              >
                Gin
              </Button>
              <Button
                key="TEQUILA"
                size="small"
                variant={filterParams.booze.tequila.variant}
                onClick={() => toggleBoozeButtons('TEQUILA')}
              >
                Tequila
              </Button>
              <Button
                key="WHISKEY"
                size="small"
                variant={filterParams.booze.whiskey.variant}
                onClick={() => toggleBoozeButtons('WHISKEY')}
              >
                Whiskey
              </Button>
              <Button
                key="RUM"
                size="small"
                variant={filterParams.booze.rum.variant}
                onClick={() => toggleBoozeButtons('RUM')}
              >
                Rum
              </Button>
              <Button
                key="SPARKLING"
                size="small"
                variant={filterParams.booze.sparkling.variant}
                onClick={() => toggleBoozeButtons('SPARKLING')}
              >
                Sparkling
              </Button>
            </Grid>
          </Grid>

          <Grid>
            <Typography
              variant="subtitle2"
              sx={{ mb: 0.75, textAlign: 'center' }}
            >
              Sort
            </Typography>
            <ButtonGroup key="sortGroup" size="small" aria-label="Sort group">
              <Button
                key="NEW"
                variant={sortButtons.new}
                onClick={() => handleSortChange('NEW')}
              >
                Newest
              </Button>
              <Button
                key="AZ"
                variant={sortButtons.az}
                onClick={() => handleSortChange('AZ')}
              >
                A {'\u2192'} Z
              </Button>
              <Button
                key="ZA"
                variant={sortButtons.za}
                onClick={() => handleSortChange('ZA')}
              >
                Z {'\u2192'} A
              </Button>
              <Button
                key="RANDOM"
                variant={sortButtons.random}
                onClick={() => handleSortChange('RANDOM')}
              >
                Random
              </Button>
            </ButtonGroup>
          </Grid>
        </Grid>

        <Divider sx={{ mt: 2, mb: 1.5 }} />

        <Grid
          container
          spacing={1}
          sx={{
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {typeof resultCount === 'number' && !shouldCollapse && (
            <Typography variant="body2" sx={{ color: 'text.secondary', mx: 1 }}>
              {resultCount} {resultCount === 1 ? 'drink' : 'drinks'}
            </Typography>
          )}
          <Button
            variant="outlined"
            color="error"
            size="small"
            disabled={!hasActiveFilters}
            onClick={() => resetFilterParams()}
          >
            Clear Filters
          </Button>
          {onAccept && (
            <Button
              variant="contained"
              color="success"
              size="small"
              onClick={() => onAccept()}
            >
              Accept
            </Button>
          )}
        </Grid>
      </Collapse>

      {shouldCollapse && !isExpanded && hasActiveFilters && (
        <Box sx={{ mt: 1, textAlign: 'center' }}>
          <Button
            variant="text"
            color="error"
            size="small"
            onClick={() => resetFilterParams()}
          >
            Clear Filters
          </Button>
        </Box>
      )}
    </Box>
  )
}
